import ColumnFilter from 'components/ui/Table/Filters/ColumnFilter'
import DateRangeColumnFilter from 'components/ui/Table/Filters/DateRangeColumnFilter'
import Table from 'components/ui/Table/Table'
import { useModal } from 'contexts/ModalContext'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { ImSpinner9 } from 'react-icons/im'
import { LuEye, LuSquarePen, LuTrash2 } from 'react-icons/lu'
import { Link } from 'wouter'

export default function CirculationCompGrid({
  user,
  data,
  handleRefresh,
  handleAnalyzeExcel,
  handleDelete
}) {
  // Hook para modal
  const { modal, openModal } = useModal()

  // Estado para datos formateados de la tabla
  const [getTableData, setTableData] = useState(null)

  // Ordenación personalizada para fechas
  const customDateSort = (rowA, rowB, columnId) => {
    let dateA = null
    let dateB = null

    // Convertir fecha de rowA en un objeto 'Date'
    if (
      typeof rowA?.values[columnId] === 'string' &&
      rowA?.values[columnId] !== '' &&
      rowA?.values[columnId] !== null &&
      rowA?.values[columnId] !== undefined
    ) {
      const parts = rowA?.values[columnId].split('/')

      if (parts.length === 3) {
        let [day, month, year] = parts.map((num) => parseInt(num, 10))
        year = year < 100 ? 2000 + year : year
        dateA = new Date(Date.UTC(year, month - 1, day, 0, 0, 0))
      }
    }

    // Convertir fecha de rowB en un objeto 'Date'
    if (
      typeof rowB?.values[columnId] === 'string' &&
      rowB?.values[columnId] !== '' &&
      rowB?.values[columnId] !== null &&
      rowB?.values[columnId] !== undefined
    ) {
      const parts = rowB?.values[columnId].split('/')

      if (parts.length === 3) {
        let [day, month, year] = parts.map((num) => parseInt(num, 10))
        year = year < 100 ? 2000 + year : year
        dateB = new Date(Date.UTC(year, month - 1, day, 0, 0, 0))
      }
    }

    // Comparar fechas
    if (isNaN(dateA)) return 1
    if (isNaN(dateB)) return -1

    return dateA - dateB
  }

  // Columnas:
  const columns = [
    {
      title: 'Filtrar por -Estatus-',
      Header: 'Estatus',
      accessor: 'Circulation_Status',
      Filter: ColumnFilter,
      showInPDF: true,
      showInExcel: true
    },
    {
      title: 'Filtrar por -Nº de Siniestro-',
      Header: 'Nº de Siniestro',
      accessor: 'Circulation_Incident_Number',
      Filter: ColumnFilter,
      showInPDF: true,
      showInExcel: true
    },
    {
      title: 'Filtrar por -Referencia agencia-',
      Header: 'Referencia agencia',
      accessor: 'Circulation_Agency_Reference',
      Filter: ColumnFilter,
      showInPDF: true,
      showInExcel: true
    },
    {
      title: 'Filtrar por -Referencia TUS-',
      Header: 'Referencia TUS',
      accessor: 'Circulation_TUS_Reference',
      Filter: ColumnFilter,
      showInPDF: true,
      showInExcel: true
    },
    {
      title: 'Filtrar por -Riesgo siniestro-',
      Header: 'Riesgo siniestro',
      accessor: 'Circulation_Incident_Risk',
      Filter: ColumnFilter,
      showInPDF: true,
      showInExcel: true
    },
    {
      title: 'Filtrar por -F. Ocurrencia-',
      Header: 'F. Ocurrencia',
      accessor: 'Circulation_Ocurrence_Date',
      Filter: DateRangeColumnFilter,
      filter: 'dateBetween', // Filtro de fechas entre
      sortType: customDateSort, // Función de ordenación personalizada para fechas
      showInPDF: true,
      showInExcel: true
    },
    {
      Header: 'Hora Ocurrencia', // Esta columna no se muestra en la tabla, pero se incluye para que aparezca al exportar a Excel
      accessor: 'Circulation_Occurrence_Time',
      showInPDF: false,
      showInExcel: true
    },
    {
      Header: 'Coordenadas', // Esta columna no se muestra en la tabla, pero se incluye para que aparezca al exportar a Excel
      accessor: 'Circulation_Coordinates',
      showInPDF: false,
      showInExcel: true
    },
    {
      title: 'Filtrar por -Conductor-',
      Header: 'Conductor',
      accessor: 'Circulation_Driver',
      Filter: ColumnFilter,
      showInPDF: true,
      showInExcel: true
    },
    {
      Header: 'Pagos', // Esta columna no se muestra en la tabla, pero se incluye para que aparezca al exportar a Excel
      accessor: 'Circulation_Payments',
      showInPDF: false,
      showInExcel: true
    },
    {
      title: 'Filtrar por -Posición-',
      Header: 'Posición',
      accessor: 'Circulation_Position',
      Filter: ColumnFilter,
      showInPDF: true,
      showInExcel: true
    },
    {
      Header: 'Descripción', // Esta columna no se muestra en la tabla, pero se incluye para que aparezca al exportar a Excel
      accessor: 'Circulation_Description',
      showInPDF: false,
      showInExcel: true
    },
    {
      Header: 'Observaciones', // Esta columna no se muestra en la tabla, pero se incluye para que aparezca al exportar a Excel
      accessor: 'Circulation_Observations',
      showInPDF: false,
      showInExcel: true
    },
    {
      title: 'Acciones',
      Header: '',
      accessor: 'Circulation_Actions',
      disableSortBy: true, // Deshabilitar ordenación
      Cell: (value) => {
        return (
          <div className="float-right px-4 py-4 text-sm whitespace-nowrap">
            <div className="flex items-center gap-x-6">
              {/* Enlace para editar o ver el registro según el rol */}
              <Link
                title={user.role_id === 1 ? 'Editar registro' : 'Ver registro'}
                href={`/tus-incidents/circulation/${value?.cell?.row?.original?.Circulation_Id}`}
                className="text-gray-500 transition-colors duration-200 dark:hover:text-yellow-500 dark:text-gray-300 hover:text-yellow-500 focus:outline-none"
              >
                {user.role_id === 1 ? (
                  <LuSquarePen size={20} />
                ) : (
                  <LuEye size={20} />
                )}
              </Link>

              {/* Botón para eliminar el registro (visible solo si es admin) */}
              {user.role_id === 1 ? (
                <button
                  title="Eliminar registro"
                  type="button"
                  className="text-gray-500 transition-colors duration-200 dark:hover:text-red-500 dark:text-gray-300 hover:text-red-500 focus:outline-none"
                  onClick={() =>
                    openModal('DeleteModal', {
                      itemName:
                        value?.cell?.row?.original?.Circulation_Incident_Number,
                      itemId: value?.cell?.row?.original?.Circulation_Id,
                      handleDelete
                    })
                  }
                >
                  <LuTrash2 size={20} />
                </button>
              ) : null}
            </div>
          </div>
        )
      },
      showInPDF: false,
      showInExcel: false
    }
  ]

  // Adaptar datos para la tabla, agregar prefijo al campo para evitar conflictos con otras tablas
  useEffect(() => {
    if (data) {
      const itemsData = []
      data?.forEach((item) => {
        // IMPORTANTE: Ordenar estos atributos igual que las columnas de la tabla para evitar desajustes en la exportación Excel/PDF
        itemsData.push({
          Circulation_Id: item.id,
          Circulation_Status: item.status,
          Circulation_Incident_Number: item.incident_number,
          Circulation_Agency_Reference: item.agency_reference,
          Circulation_TUS_Reference: item.tus_reference,
          Circulation_Incident_Risk: item.incident_risk,
          Circulation_Ocurrence_Date: item.occurrence_date,
          Circulation_Occurrence_Time: item.occurrence_time,
          Circulation_Coordinates: item.coordinates,
          Circulation_Driver: item.driver,
          Circulation_Payments: item.payments,
          Circulation_Position: item.position,
          Circulation_Description: item.description,
          Circulation_Observations: item.observations,
          Circulation_Type: item.type
        })
      })
      setTableData(itemsData)
    }
  }, [data])

  return getTableData ? (
    <Table
      tableName={'Circulación'}
      columns={columns}
      columnsToWrap={['Circulation_Description', 'Circulation_Observations']}
      createNewItemURL={'/tus-incidents/circulation/new'}
      data={getTableData}
      defaultSort={{ id: 'Circulation_Ocurrence_Date', desc: true }} // Ordenación por 'F. Ocurrencia' descendente por defecto
      hiddenColumns={[
        'Circulation_Occurrence_Time',
        'Circulation_Coordinates',
        'Circulation_Payments',
        'Circulation_Description',
        'Circulation_Observations'
      ]}
      allowImportExcel={true}
      uploadExcelURL={`incidents/circulation/${user.id}`}
      handleRefresh={handleRefresh}
      handleAnalyzeExcel={handleAnalyzeExcel}
    />
  ) : (
    <div className="flex items-center content-center justify-center w-full h-full">
      <p
        className="flex flex-col-reverse items-center justify-center h-full font-bold dark:text-[#F5F5F5] text-gray-700"
        title="Cargando..."
      >
        <ImSpinner9 size={70} className="animate-spin" />
      </p>
    </div>
  )
}

CirculationCompGrid.propTypes = {
  user: PropTypes.object,
  data: PropTypes.array,
  handleRefresh: PropTypes.func.isRequired,
  handleAnalyzeExcel: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired
}
